import { useEffect } from 'react';
import isEmpty from 'lodash.isempty';
import { useInView } from 'react-intersection-observer';

// types
import type { MiVideoContainerProps } from 'components/Video/miVideo/MiVideoContainer';

// components
import VideoPlayButton from 'components/Video/VideoPlayButton';
import PreviewImageContent from 'components/Video/PreviewImageContent';
import MiVideoView from './MiVideoView';

// utils
import { useUCConsent, VideoConsent } from 'features/cookie-consent';
import { useIsMobile } from 'components/App/SizeProvider';

interface MiVideoContainerViewProps {
  displayVideo: boolean;
  previewImageDisplay?: string;
  domId: string | null;
  playVideo?: (e?: any) => void;
  handleInView?: (e: boolean) => void;
  paused: boolean;
  hasModal: boolean;
  videoPlayButtonLabel: string;
  buttonText: string;
  hasError: boolean;
  isModal: boolean;
  previewImage: string;
  content: MiVideoContainerProps['content'];
  className: string;
  openModal: () => void;
  isSectionReference: boolean;
  isVideoContent: boolean;
}

export const VIDEO_VARIANT_BUTTON_ONLY = 'buttonOnly';
const VIDEO_VARIANT_BUTTON_TEXT = 'buttonText';

const MiVideoContainerView = ({
  previewImage,
  displayVideo,
  videoPlayButtonLabel = 'Play Video',
  paused,
  content,
  previewImageDisplay,
  playVideo,
  className,
  hasModal,
  openModal,
  buttonText,
  domId,
  hasError,
  isModal,
  isSectionReference,
  isVideoContent = false,
  handleInView = (e: boolean) => {},
}: MiVideoContainerViewProps) => {
  const isMobile = useIsMobile();
  const { videoTitle, autoplay, subheadline, textColor, videoVariant, video, pictureAlt } = content;
  const ucConsent = useUCConsent();

  const { ref: refMiVideoContainer, inView } = useInView({
    threshold: 0,
  });

  useEffect(() => {
    handleInView(inView);
  }, [inView]);

  if (isEmpty(video)) return null;
  const showPreviewImage = !isModal || (!displayVideo && autoplay === 'no');
  const hasButtonText = videoVariant && videoVariant === VIDEO_VARIANT_BUTTON_TEXT;
  const hasPlayButton = videoVariant && videoVariant === VIDEO_VARIANT_BUTTON_ONLY;
  const allowedToHaveButton = (autoplay !== 'yes' && !isModal && !hasError) || isMobile;
  const hasText = videoTitle || subheadline;
  const styles = showPreviewImage
    ? {
        style: {
          backgroundImage: `url(${previewImage})`,
        },
      }
    : {};

  if (ucConsent.Comfort === false) {
    return (
      <div className={`${className}__video`}>
        <VideoConsent type={'mi24'} />
      </div>
    );
  }
  return (
    <div ref={refMiVideoContainer} className={`${className}__video`}>
      <div
        className={`${className}__video-module`}
        role="img"
        aria-label={pictureAlt}
        title={pictureAlt}
        {...styles}
      >
        {(hasModal || hasButtonText || (!hasModal && hasText)) && showPreviewImage && paused && (
          <PreviewImageContent
            subheadline={subheadline}
            textColor={textColor}
            className={className}
            playVideo={hasModal ? openModal : playVideo}
            videoTitle={videoTitle}
            videoPlayButtonLabel={videoPlayButtonLabel}
            buttonText={buttonText}
            allowedToHaveButton={allowedToHaveButton}
            isPreview={undefined}
            isSectionReference={isSectionReference}
            isVideoContent={isVideoContent}
            videoVariant={videoVariant}
          />
        )}
        {!hasModal && (
          <div
            className={`video-wrapper${
              displayVideo && ucConsent.Comfort === true ? ' show-video' : ''
            }`}
          >
            <MiVideoView
              previewImageDisplay={previewImageDisplay}
              videoId={video?.videoId}
              domId={domId}
              className={className}
            />
            {((hasPlayButton && showPreviewImage) ||
              (!hasButtonText && !hasText && !displayVideo) ||
              (displayVideo && paused && !isMobile)) &&
              ucConsent.Comfort &&
              (paused || !hasModal) &&
              allowedToHaveButton &&
              className !== 'c-text-video' &&
              className !== 'hero-image-c' &&
              className !== 'c-video-content' && (
                <VideoPlayButton
                  videoPlayButtonLabel={videoPlayButtonLabel}
                  playVideo={playVideo}
                />
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MiVideoContainerView;
